/**
 * Code block chrome: a header widget on every fence (language badge + copy
 * button) and automatic language labeling — when an unlabeled block's
 * content is recognizable and the cursor leaves it, the fence quietly gains
 * its language (honest markdown: the ``` line is really edited, so it
 * highlights everywhere, including other apps).
 */
import { EditorView, ViewPlugin, type ViewUpdate, WidgetType, Decoration, type DecorationSet } from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
import { syntaxTree } from "@codemirror/language";
import { detectLanguage } from "./lang-detect";

class CodeHeaderWidget extends WidgetType {
  constructor(
    readonly lang: string,
    readonly codeFrom: number,
    readonly codeTo: number,
  ) {
    super();
  }
  override eq(other: CodeHeaderWidget) {
    return other.lang === this.lang && other.codeFrom === this.codeFrom && other.codeTo === this.codeTo;
  }
  toDOM(view: EditorView) {
    const wrap = document.createElement("span");
    wrap.className = "hn-code-header";
    const label = document.createElement("span");
    label.className = "hn-code-lang";
    label.textContent = this.lang || "plain";
    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "hn-code-copy";
    copy.textContent = "copy";
    copy.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const text = view.state.sliceDoc(this.codeFrom, this.codeTo);
      void navigator.clipboard.writeText(text).then(() => {
        copy.textContent = "copied";
        setTimeout(() => (copy.textContent = "copy"), 1200);
      });
    });
    wrap.append(label, copy);
    return wrap;
  }
  override ignoreEvent() {
    return true;
  }
}

interface FenceInfo {
  nodeFrom: number;
  nodeTo: number;
  infoFrom: number;
  infoText: string;
  codeFrom: number;
  codeTo: number;
}

function collectFences(view: EditorView): FenceInfo[] {
  const out: FenceInfo[] = [];
  const tree = syntaxTree(view.state);
  for (const { from, to } of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: (node) => {
        if (node.name !== "FencedCode") return;
        const n = node.node;
        const info = n.getChild("CodeInfo");
        const text = n.getChild("CodeText");
        const firstLine = view.state.doc.lineAt(node.from);
        out.push({
          nodeFrom: node.from,
          nodeTo: node.to,
          infoFrom: info?.from ?? Math.min(node.from + 3, firstLine.to),
          infoText: info ? view.state.sliceDoc(info.from, info.to) : "",
          codeFrom: text?.from ?? firstLine.to + 1,
          codeTo: text?.to ?? node.to,
        });
      },
    });
  }
  return out;
}

const codeHeaderPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = this.build(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged || syntaxTree(u.state) !== syntaxTree(u.startState)) {
        this.decorations = this.build(u.view);
      }
    }
    build(view: EditorView): DecorationSet {
      const builder = new RangeSetBuilder<Decoration>();
      for (const fence of collectFences(view)) {
        const firstLineEnd = view.state.doc.lineAt(fence.nodeFrom).to;
        builder.add(
          firstLineEnd,
          firstLineEnd,
          Decoration.widget({
            widget: new CodeHeaderWidget(fence.infoText, fence.codeFrom, fence.codeTo),
            side: 1,
          }),
        );
      }
      return builder.finish();
    }
  },
  { decorations: (v) => v.decorations },
);

/** Auto-label unlabeled fences once the cursor leaves them. */
const autoLabelPlugin = ViewPlugin.fromClass(
  class {
    private wasInside = new Set<number>();

    update(u: ViewUpdate) {
      if (!u.selectionSet && !u.docChanged) return;
      const view = u.view;
      const head = view.state.selection.main.head;
      const fences = collectFences(view);
      const nowInside = new Set<number>();

      for (const fence of fences) {
        if (head >= fence.nodeFrom && head <= fence.nodeTo) nowInside.add(fence.nodeFrom);
      }

      // fences the cursor just LEFT, still unlabeled -> try to detect
      for (const fence of fences) {
        if (nowInside.has(fence.nodeFrom)) continue;
        if (!this.wasInside.has(fence.nodeFrom)) continue;
        if (fence.infoText) continue;
        const code = view.state.sliceDoc(fence.codeFrom, fence.codeTo);
        const lang = detectLanguage(code);
        if (lang) {
          queueMicrotask(() => {
            view.dispatch({
              changes: { from: fence.infoFrom, insert: lang },
              userEvent: "input.autolang",
            });
          });
        }
      }
      this.wasInside = nowInside;
    }
  },
);

export const codeBlockExtras = [
  codeHeaderPlugin,
  autoLabelPlugin,
  EditorView.theme({
    ".hn-code-header": {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      marginLeft: "12px",
      verticalAlign: "baseline",
      userSelect: "none",
    },
    ".hn-code-lang": {
      fontSize: "10px",
      fontFamily: "var(--font-ui)",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: "var(--text-muted)",
      background: "var(--surface-active)",
      borderRadius: "3px",
      padding: "1px 6px",
    },
    ".hn-code-copy": {
      fontSize: "10px",
      fontFamily: "var(--font-ui)",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: "var(--text-muted)",
      background: "transparent",
      border: "1px solid var(--border-strong)",
      borderRadius: "3px",
      padding: "1px 6px",
      cursor: "pointer",
      opacity: "0",
      transition: "opacity 80ms",
    },
    ".hn-codeblock:hover .hn-code-copy, .hn-code-copy:focus": {
      opacity: "1",
    },
    ".hn-code-copy:hover": {
      color: "var(--text-primary)",
      borderColor: "var(--accent)",
    },
  }),
];
